<template>
  <section id="aos-section3" class="relative flex size-full flex-col items-center overflow-hidden px-5">
    <OptimizedImage
      base-path="image/home/bg_gradation_dark01"
      file-name="bg_gradation_dark01"
      alt="bg_gradation_dark01"
      img-format="jpg"
      image-class="absolute left-0 top-0 size-full object-cover"
    />

    <div class="z-10 flex flex-col divide-y divide-slate-500 whitespace-pre-line text-center">
      <!-- 체크리스트 영역 -->
      <div class="flex flex-col items-center gap-5 pb-20 pt-[60px] lg:gap-20 lg:pt-[120px]">
        <i18n-t keypath="main.section3.title1" tag="p" data-aos="fade-up" class="gn-text-3xl font-bold text-white">
          <template #title1-highlight>
            <span class="font-extrabold text-blue-300">{{ $t('main.section3.title1-highlight') }}</span>
          </template>
        </i18n-t>

        <div class="reactive flex w-[90vw] justify-center">
          <div class="lg:reactive static backdrop-blur-0">
            <div
              data-aos="fade-up"
              data-aos-delay="400"
              data-aos-anchor="#aos-section3"
              class="rounded-3xl bg-white/80 px-3 py-5 lg:px-14 lg:py-12"
            >
              <div class="flex items-center gap-3 pb-4 lg:pb-10">
                <CheckCircleIcon class="size-3 text-blue-500 lg:size-8" aria-hidden="true" />
                <span class="gn-text-sm sm:gn-text-2xl font-extrabold">
                  {{ $t(`main.section3.checklist.title`) }}
                </span>
              </div>

              <div class="flex flex-col gap-2 lg:gap-5">
                <div v-for="num in 5" :key="`check${num}`" class="relative flex items-start gap-3 text-start">
                  <div class="mt-0.5 aspect-square size-3 border border-slate-400 bg-white lg:size-[22px]" />
                  <span class="gn-text-xs sm:gn-text-xl font-bold">
                    {{ $t(`main.section3.checklist.check${num}`) }}
                  </span>
                  <img
                    data-aos="fade"
                    :data-aos-delay="1000 + num * 200"
                    data-aos-anchor="#aos-section3"
                    src="assets/svg/icon_check_blue.svg"
                    alt="icon_check_blue"
                    class="absolute -top-1 left-0 size-4 lg:-top-2 lg:size-8"
                  />
                </div>
              </div>
            </div>

            <img
              src="assets/image/home/img_index_pencil_red.png"
              alt="img_index_pencil_red"
              data-aos="fade-up"
              data-aos-delay="1000"
              data-aos-anchor="#aos-section3"
              class="absolute -top-6 right-[10px] h-[51px] object-contain lg:-right-[10%] lg:-top-[60px] lg:h-[130px]"
            />

            <picture>
              <source
                media="(min-width: 1024px)"
                :srcset="useDynamicImage(`image/home/index_mother/img_index_mother02${localImage}.png`)"
              />
              <source :srcset="useDynamicImage(`image/home/index_mother/img_index_mother02${localImage}_md.png`)" />
              <img
                :src="useDynamicImage('image/home/index_mother/img_index_mother02.png')"
                alt="img_index_mother02"
                data-aos="fade-up"
                data-aos-delay="1000"
                data-aos-anchor="#aos-section3"
                class="absolute -bottom-20 -right-[5px] h-[122px] lg:-bottom-20 lg:-left-[250px] lg:h-[343px]"
              />
            </picture>
          </div>
        </div>
      </div>
      <!-- 뉴스 카드 영역 -->
      <div
        id="card-news"
        class="flex flex-col items-center gap-5 overflow-hidden pb-[60px] pt-10 lg:gap-20 lg:py-[120px]"
      >
        <i18n-t keypath="main.section3.title2" tag="p" data-aos="fade-up" class="gn-text-3xl font-bold text-white">
          <template #title2-highlight>
            <span class="font-extrabold text-blue-300">{{ $t('main.section3.title2-highlight') }}</span>
          </template>
        </i18n-t>

        <div :class="['relative w-full lg:h-[420px]', locale === 'ko' ? 'h-[305px]' : 'h-[250px]']">
          <div
            v-for="(item, index) in newsCards"
            :key="index"
            data-aos="fade-up"
            data-aos-anchor="#card-news"
            :data-aos-delay="200 + 400 * (index + 1)"
            :class="[
              'absolute flex flex-col items-start gap-1.5 lg:gap-4 ',
              'rounded-2xl border border-slate-200 p-3 text-start shadow backdrop-blur lg:p-7',
              item.cardClass
            ]"
          >
            <img
              :class="[item.imgClass, 'object-contain']"
              :src="useDynamicImage(`image/home/img_logo/img_logo_news0${item.imgId}${localImage}.png`)"
            />
            <div class="flex flex-col gap-1.5 lg:gap-3">
              <span :class="['gn-text-xs lg:gn-text-2xl break-keep font-bold', {[item.enFontClass]: locale === 'en'}]">
                {{ $t(`main.section3.newsList.news${item.imgId}.title`) }}
              </span>
              <span v-if="locale === 'ko'" class="gn-text-xs lg:gn-text-sm text-slate-500">
                {{ $t(`main.section3.newsList.news${item.imgId}.date`) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import OptimizedImage from '~/components/common/OptimizedImage.vue'
import {CheckCircleIcon} from '@heroicons/vue/20/solid'

const {locale} = useI18n()
const localImage = computed(() => (locale.value === 'ko' ? '' : '_en'))

/** 뉴스 데이터 배열 */
const newsCards = [
  {
    imgId: 1,
    imgClass: 'h-4 lg:h-[29px]',
    cardClass: 'z-20 w-[70vw] lg:w-[44vw] bg-white/90 left-0 top-0 lg:left-[20%] lg:top-[0%]',
    enFontClass: 'font-[Literata]'
  },
  {
    imgId: 3,
    imgClass: 'h-5 lg:h-9',
    cardClass: 'z-10 w-[60vw] lg:w-[50vw] bg-white/90 right-0 top-1/4 lg:left-[5%] lg:top-[30%]',
    enFontClass: 'font-extrabold'
  },
  {
    imgId: 4,
    imgClass: 'h-[13px] lg:h-6',
    cardClass: 'z-20 w-[50vw] lg:w-[40vw] bg-white/90 left-[2%] top-2/4 lg:left-[22%] lg:top-[260px]',
    enFontClass: 'font-serif'
  },
  {
    imgId: 2,
    imgClass: 'h-4 lg:h-[29px] bg-[#155f8e]',
    cardClass: 'z-40 w-[55vw] lg:w-[31vw] bg-white top-3/4 right-0 lg:right-[2%] lg:top-[160px]',
    enFontClass: 'font-[Arial]'
  }
]
</script>

<style lang="scss"></style>
